import { Outlet, useLocation } from 'react-router-dom';
import * as Dialog from '@radix-ui/react-dialog';
import { Sidebar, SIDEBAR_WIDTH, SIDEBAR_COLLAPSED_WIDTH } from './Sidebar/Sidebar';
import { Topbar } from './Topbar/Topbar';
import { BrandBanner } from './BrandBanner';
import { ALL_NAV_ITEMS } from './Sidebar/navItems';
import { useUiStore } from '../../store/ui.store';
import { TooltipProvider } from '../ui/Tooltip';

function pageTitle(pathname: string): string {
  const match = ALL_NAV_ITEMS.find((item) => pathname === item.path || pathname.startsWith(item.path + '/'));
  return match?.label ?? 'Multi-PERT';
}

/**
 * Desktop gets a persistent sidebar column (`hidden md:block`); mobile gets
 * the same <Sidebar> content inside a Radix Dialog styled as a slide-in
 * panel — Tailwind's responsive classes handle which one is visible.
 *
 * BrandBanner sits above the sidebar+content row entirely, full page width —
 * the whole shell is one fixed-height column (banner + row) instead of the
 * page itself scrolling, so only <main> scrolls.
 */
export function AppLayout() {
  const location = useLocation();
  const mobileSidebarOpen = useUiStore((s) => s.mobileSidebarOpen);
  const setMobileSidebarOpen = useUiStore((s) => s.setMobileSidebarOpen);
  const sidebarCollapsed = useUiStore((s) => s.sidebarCollapsed);

  return (
    <TooltipProvider>
      <div className="flex h-screen flex-col bg-bg">
        <BrandBanner />

        <div className="flex min-h-0 flex-1">
          <aside
            className="hidden h-full shrink-0 border-r border-border bg-surface transition-[width] duration-150 md:block"
            style={{ width: sidebarCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH }}
          >
            <Sidebar />
          </aside>

          <Dialog.Root open={mobileSidebarOpen} onOpenChange={setMobileSidebarOpen}>
            <Dialog.Portal>
              <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40 md:hidden" />
              <Dialog.Content
                className="fixed inset-y-0 left-0 z-50 h-screen bg-surface shadow-xl outline-none md:hidden"
                style={{ width: SIDEBAR_WIDTH }}
                aria-describedby={undefined}
              >
                <Dialog.Title className="sr-only">Navigation</Dialog.Title>
                <Sidebar onNavigate={() => setMobileSidebarOpen(false)} />
              </Dialog.Content>
            </Dialog.Portal>
          </Dialog.Root>

          <div className="flex min-w-0 flex-1 flex-col">
            <Topbar title={pageTitle(location.pathname)} onMenuClick={() => setMobileSidebarOpen(true)} />
            <main className="flex min-w-0 flex-1 flex-col overflow-y-auto p-4 md:p-6">
              <Outlet />
            </main>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
}
